import { chartNum } from '@/lib/recharts-format';
import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { GlassCard, GlassCardContent, GlassCardHeader, GlassCardTitle } from '@easyflow/ui-master/components/ui/glass-card';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Input } from '@easyflow/ui-master/components/ui/input';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { Progress } from '@easyflow/ui-master/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-master/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@easyflow/ui-master/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@easyflow/ui-master/components/ui/dialog';
import { Label } from '@easyflow/ui-master/components/ui/label';
import {
  HardDrive, CloudCog, BarChart3, Users, AlertTriangle, Search,
  RefreshCw, Download, CheckCircle2, XCircle, Clock, Activity,
  FileText, Video, Mic, TrendingUp, Settings, Eye,
  Trash2, Database, Zap, Filter, ChevronDown, Info, FolderOpen
} from 'lucide-react';
// Task #1237 — Tab DB Quotas
import DBQuotasTab from '@/components/storage/DBQuotasTab';
// File Browser R2 (read-only, stile Finder/Explorer)
import FileBrowserTab from '@/components/storage/FileBrowserTab';
import {
  PieChart, Pie, Cell, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import apiClient from '@/lib/api';
import { useToast } from '@/hooks/use-toast';

const COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#10b981', '#f59e0b', '#ef4444', '#14b8a6', '#f97316'];

const CATEGORY_LABELS: Record<string, string> = {
  recording: 'Registrazioni',
  recordings: 'Registrazioni',
  video: 'Video',
  videos: 'Video',
  document: 'Documenti',
  documents: 'Documenti'
};

const CATEGORY_ICONS: Record<string, React.ElementType> = {
  recording: Mic,
  recordings: Mic,
  video: Video,
  videos: Video,
  document: FileText,
  documents: FileText
};

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

interface OverviewData {
  totalUsedBytes: number;
  totalUsedFormatted: string;
  totalFiles: number;
  syncPending: number;
  syncPendingBytes: number;
  syncErrors24h: number;
  estimatedCost: number;
  breakdown: { recordings: number; videos: number; documents: number };
  topTenants: Array<{
    tenantId: string; tenantName: string; tenantSlug: string;
    usedBytes: number; maxBytes: number; percentage: number;
    recordings: number; videos: number; documents: number;
  }>;
  r2Status: { status: string; latencyMs: number; lastCheck: string } | null;
}

interface ConfigData {
  configured: boolean;
  endpoint?: string | null;
  accountId: string | null;
  accessKeyId: string | null;
  secretAccessKey: string | null;
  bucketName: string | null;
  lastHealthCheck: {
    status: string; latencyMs: number; error: string | null; checkedAt: string;
  } | null;
}

interface TenantRow {
  tenantId: string; tenantName: string; tenantSlug: string;
  maxBytes: number; usedBytes: number; percentage: number; alertLevel: string;
  recordings: number; videos: number; documents: number;
  recordingsEnabled: boolean;
  recordingsMaxBytes: number;
  lastSyncAt: string | null; syncErrors24h: number; lastAlertSent: string | null;
}

interface AlertRow {
  id: string; alertType: string; severity: string; title: string;
  details: Record<string, unknown> | null; resolved: boolean; resolvedAt: string | null;
  createdAt: string; tenantId?: string; tenantName?: string;
}

interface StoragePagination {
  page: number; limit: number; total: number; totalPages: number;
}

function errMsg(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

interface AnalyticsData {
  categoryBreakdown: Array<{ category: string; fileCount: number; totalBytes: number; totalFormatted: string }>;
  dailyUploads: Array<{ day: string; category: string; fileCount: number; totalBytes: number }>;
  avgFileSize: Array<{ category: string; avgSize: number; avgSizeFormatted: string; count: number }>;
  topFiles: Array<{
    id: string; fileName: string; fileSize: number; fileSizeFormatted: string;
    category: string; createdAt: string; cloudSynced: boolean;
    tenantId: string; tenantName: string;
  }>;
}

export default function StorageMaster() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'overview';
  const [selectedTab, setSelectedTab] = useState(initialTab);
  const [isLoading, setIsLoading] = useState(true);

  const [config, setConfig] = useState<ConfigData | null>(null);
  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [tenantsPagination, setTenantsPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [alerts, setAlerts] = useState<AlertRow[]>([]);
  const [testingConnection, setTestingConnection] = useState(false);
  const [tenantSearch, setTenantSearch] = useState('');
  const [tenantFilter, setTenantFilter] = useState('all');
  const [tenantSort, setTenantSort] = useState('used_bytes');
  const [tenantSortDir, setTenantSortDir] = useState('desc');
  const [alertTenantFilter, setAlertTenantFilter] = useState('all');
  const [alertSeverityFilter, setAlertSeverityFilter] = useState('all');
  const [analyticsScope, setAnalyticsScope] = useState('global');
  const [analyticsTenantId, setAnalyticsTenantId] = useState('');
  const [allTenantsList, setAllTenantsList] = useState<Array<{ id: string; name: string }>>([]);

  const [quotaDialogOpen, setQuotaDialogOpen] = useState(false);
  const [quotaDialogTenant, setQuotaDialogTenant] = useState<TenantRow | null>(null);
  const [quotaForm, setQuotaForm] = useState({
    maxBytes: 0, recordingsMaxBytes: 0,
    recordingsEnabled: true,
    recordingsRetentionDays: 90
  });

  const [r2Form, setR2Form] = useState({
    accountId: '', accessKeyId: '', secretAccessKey: '', bucketName: '', endpoint: ''
  });
  const [savingR2Config, setSavingR2Config] = useState(false);
  const [showR2Form, setShowR2Form] = useState(false);

  const handleTabChange = useCallback((tab: string) => {
    setSelectedTab(tab);
    setSearchParams({ tab });
  }, [setSearchParams]);

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['settings', 'overview', 'analytics', 'tenants', 'files', 'alerts'].includes(tabParam)) {
      setSelectedTab(tabParam);
    }
  }, [searchParams]);

  const loadConfig = useCallback(async () => {
    try {
      const res = await apiClient.get<ConfigData>('/master/storage/config');
      if (res.success && res.data) setConfig(res.data);
    } catch {}
  }, []);

  const loadOverview = useCallback(async () => {
    try {
      const res = await apiClient.get<OverviewData>('/master/storage/overview');
      if (res.success && res.data) setOverview(res.data);
    } catch {}
  }, []);

  const loadAnalytics = useCallback(async () => {
    try {
      const params = analyticsScope === 'tenant' && analyticsTenantId ? `?tenantId=${analyticsTenantId}` : '';
      const res = await apiClient.get<AnalyticsData>(`/master/storage/analytics${params}`);
      if (res.success && res.data) setAnalytics(res.data);
    } catch {}
  }, [analyticsScope, analyticsTenantId]);

  const loadTenants = useCallback(async (page = 1) => {
    try {
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('limit', '20');
      params.set('sortBy', tenantSort);
      params.set('sortDir', tenantSortDir);
      if (tenantSearch) params.set('search', tenantSearch);
      if (tenantFilter !== 'all') params.set('filter', tenantFilter);

      const res = await apiClient.get<{ tenants: TenantRow[]; pagination: StoragePagination }>(`/master/storage/tenants?${params}`);
      if (res.success && res.data) {
        setTenants(res.data.tenants);
        setTenantsPagination(res.data.pagination);
      }
    } catch {}
  }, [tenantSearch, tenantFilter, tenantSort, tenantSortDir]);

  const loadAlerts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (alertTenantFilter !== 'all') params.set('tenantId', alertTenantFilter);
      if (alertSeverityFilter !== 'all') params.set('severity', alertSeverityFilter);
      const res = await apiClient.get<{ alerts: AlertRow[]; pagination: StoragePagination }>(`/master/storage/alerts?${params}`);
      if (res.success && res.data) setAlerts(res.data.alerts || []);
    } catch {}
  }, [alertTenantFilter, alertSeverityFilter]);

  const loadAllTenantsList = useCallback(async () => {
    try {
      const res = await apiClient.get<{ tenants: Array<{ id: string; name: string }> }>('/master/tenants');
      if (res.success && res.data) setAllTenantsList(res.data.tenants || (Array.isArray(res.data) ? res.data as Array<{ id: string; name: string }> : []));
    } catch {}
  }, []);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      await Promise.all([loadConfig(), loadOverview(), loadAllTenantsList()]);
      setIsLoading(false);
    };
    void load();
  }, [loadConfig, loadOverview, loadAllTenantsList]);

  useEffect(() => {
    if (selectedTab === 'analytics') void loadAnalytics();
  }, [selectedTab, loadAnalytics]);

  useEffect(() => {
    if (selectedTab === 'tenants') void loadTenants();
  }, [selectedTab, loadTenants]);

  useEffect(() => {
    if (selectedTab === 'alerts') void loadAlerts();
  }, [selectedTab, loadAlerts]);

  const handleTestConnection = async () => {
    setTestingConnection(true);
    try {
      const res = await apiClient.post<{ status: string; latencyMs: number; error?: string }>('/master/storage/config/test');
      if (res.success && res.data) {
        if (res.data.status === 'connected') {
          toast({ title: 'Connessione R2 OK', description: `Latenza: ${res.data.latencyMs}ms` });
        } else {
          toast({ title: 'Connessione R2 Fallita', description: res.data.error || 'Errore sconosciuto', variant: 'destructive' });
        }
        await loadConfig();
      }
    } catch (err: unknown) {
      toast({ title: 'Errore', description: errMsg(err), variant: 'destructive' });
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSaveR2Config = async () => {
    if (!r2Form.accessKeyId || !r2Form.secretAccessKey || !r2Form.bucketName) {
      toast({ title: 'Errore', description: 'Access Key ID, Secret Access Key e Bucket Name sono obbligatori', variant: 'destructive' });
      return;
    }
    if (!r2Form.endpoint && !r2Form.accountId) {
      toast({ title: 'Errore', description: 'Inserire Endpoint S3 oppure Account ID', variant: 'destructive' });
      return;
    }
    setSavingR2Config(true);
    try {
      const res = await apiClient.put<{ latencyMs?: number }>('/master/storage/config', r2Form);
      if (res.success) {
        toast({ title: 'Configurazione R2 salvata', description: `Connessione verificata (${res.data?.latencyMs}ms)` });
        setShowR2Form(false);
        setR2Form({ accountId: '', accessKeyId: '', secretAccessKey: '', bucketName: '', endpoint: '' });
        await loadConfig();
      }
    } catch (err: unknown) {
      toast({ title: 'Errore configurazione R2', description: errMsg(err), variant: 'destructive' });
    } finally {
      setSavingR2Config(false);
    }
  };

  const handleForceSync = async (tenantId: string) => {
    try {
      await apiClient.post(`/master/storage/tenants/${tenantId}/force-sync`);
      toast({ title: 'Sync avviato', description: 'Sincronizzazione forzata avviata' });
    } catch (err: unknown) {
      toast({ title: 'Errore', description: errMsg(err), variant: 'destructive' });
    }
  };

  const handleForceCleanup = async (tenantId: string) => {
    try {
      await apiClient.post(`/master/storage/tenants/${tenantId}/force-cleanup`);
      toast({ title: 'Cleanup avviato', description: 'Cleanup forzato avviato' });
    } catch (err: unknown) {
      toast({ title: 'Errore', description: errMsg(err), variant: 'destructive' });
    }
  };

  const handleRecalculate = async (tenantId: string) => {
    try {
      await apiClient.post(`/master/storage/tenants/${tenantId}/recalculate`);
      toast({ title: 'Ricalcolo completato' });
      void loadTenants(tenantsPagination.page);
    } catch (err: unknown) {
      toast({ title: 'Errore', description: errMsg(err), variant: 'destructive' });
    }
  };

  const openQuotaDialog = (tenant: TenantRow) => {
    setQuotaDialogTenant(tenant);
    setQuotaForm({
      maxBytes: tenant.maxBytes,
      recordingsMaxBytes: tenant.recordingsMaxBytes,

      recordingsEnabled: tenant.recordingsEnabled,
      recordingsRetentionDays: 90
    });
    setQuotaDialogOpen(true);
  };

  const handleSaveQuota = async () => {
    if (!quotaDialogTenant) return;
    try {
      await apiClient.put(`/master/storage/tenants/${quotaDialogTenant.tenantId}/quota`, {
        maxBytes: quotaForm.maxBytes,
        recordingsMaxBytes: quotaForm.recordingsMaxBytes,
        recordingsEnabled: quotaForm.recordingsEnabled,
        recordingsRetentionDays: quotaForm.recordingsRetentionDays
      });
      toast({ title: 'Quota aggiornata' });
      setQuotaDialogOpen(false);
      void loadTenants(tenantsPagination.page);
    } catch (err: unknown) {
      toast({ title: 'Errore', description: errMsg(err), variant: 'destructive' });
    }
  };

  const handleExportCSV = () => {
    const headers = ['Tenant', 'Quota', 'Usato', '%', 'Registrazioni', 'Documenti', 'Video'];
    const rows = tenants.map(t => [
      t.tenantName, formatBytes(t.maxBytes), formatBytes(t.usedBytes),
      t.percentage + '%', formatBytes(t.recordings),
      formatBytes(t.documents), formatBytes(t.videos)
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `storage-tenants-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    toast({ title: 'Export CSV completato' });
  };

  const alertLevelBadge = (level: string) => {
    switch (level) {
      case 'critical': return <Badge variant="destructive">Critico</Badge>;
      case 'warning': return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Warning</Badge>;
      case 'ok': return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">OK</Badge>;
      case 'empty': return <Badge variant="outline">Vuoto</Badge>;
      default: return <Badge variant="outline">{level}</Badge>;
    }
  };

  const severityBadge = (severity: string) => {
    switch (severity) {
      case 'critical': return <Badge variant="destructive">Critico</Badge>;
      case 'high': return <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">Alto</Badge>;
      case 'warning': return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Warning</Badge>;
      case 'info': return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">Info</Badge>;
      default: return <Badge variant="outline">{severity}</Badge>;
    }
  };

  const breakdownPieData = overview ? [
    { name: 'Registrazioni', value: overview.breakdown.recordings },
    { name: 'Video', value: overview.breakdown.videos },
    { name: 'Documenti', value: overview.breakdown.documents }
  ].filter(d => d.value > 0) : [];

  const analyticsPieData = analytics?.categoryBreakdown?.map(c => ({
    name: CATEGORY_LABELS[c.category] || c.category,
    value: c.totalBytes
  })).filter(d => d.value > 0) || [];

  const dailyUploadAggregated = (() => {
    if (!analytics?.dailyUploads?.length) return [];
    const byDay: Record<string, Record<string, number>> = {};
    for (const row of analytics.dailyUploads) {
      const bucket = byDay[row.day] ?? (byDay[row.day] = {});
      bucket[row.category] = (bucket[row.category] ?? 0) + row.fileCount;
    }
    return Object.entries(byDay).map(([day, cats]) => ({ day, ...cats }));
  })();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Storage Manager
          </h1>
          <p className="text-muted-foreground dark:text-muted-foreground/70 mt-1">Gestione centralizzata storage R2 e quota tenant</p>
        </div>
        <Button variant="outline" onClick={() => { void loadConfig(); void loadOverview(); }}>
          <RefreshCw className="mr-2 h-4 w-4" /> Refresh
        </Button>
      </div>

      <Tabs value={selectedTab} onValueChange={handleTabChange}>
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="settings"><Settings className="mr-2 h-4 w-4" />R2 Settings</TabsTrigger>
          <TabsTrigger value="overview"><HardDrive className="mr-2 h-4 w-4" />Overview</TabsTrigger>
          <TabsTrigger value="analytics"><BarChart3 className="mr-2 h-4 w-4" />Analytics</TabsTrigger>
          <TabsTrigger value="tenants"><Users className="mr-2 h-4 w-4" />Tenants R2</TabsTrigger>
          {/* Task #1237 — Tab quote DB per tenant */}
          <TabsTrigger value="db-quotas"><Database className="mr-2 h-4 w-4" />Quote DB</TabsTrigger>
          <TabsTrigger value="files"><FolderOpen className="mr-2 h-4 w-4" />Esplora File</TabsTrigger>
          <TabsTrigger value="alerts"><AlertTriangle className="mr-2 h-4 w-4" />Alerts Log</TabsTrigger>
        </TabsList>

        {/* TAB: File Browser R2 (read-only) */}
        <TabsContent value="files" className="space-y-6">
          <FileBrowserTab />
        </TabsContent>

        {/* Task #1237 — TAB: DB Quotas per tenant */}
        <TabsContent value="db-quotas" className="space-y-6">
          <DBQuotasTab />
        </TabsContent>

        {/* TAB: R2 Settings */}
        <TabsContent value="settings" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <GlassCard>
              <GlassCardHeader>
                <GlassCardTitle className="flex items-center gap-2">
                  <CloudCog className="h-5 w-5" /> Stato Connessione R2
                </GlassCardTitle>
              </GlassCardHeader>
              <GlassCardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-muted-foreground">Stato:</span>
                  {config?.configured ? (
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                      <CheckCircle2 className="mr-1 h-3 w-3" /> Configurato
                    </Badge>
                  ) : (
                    <Badge variant="destructive">
                      <XCircle className="mr-1 h-3 w-3" /> Non Configurato
                    </Badge>
                  )}
                </div>

                {config?.configured && !showR2Form ? (
                  <>
                    <div className="space-y-2 text-sm">
                      {config?.endpoint && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Endpoint:</span>
                          <span className="font-mono text-xs">{config.endpoint}</span>
                        </div>
                      )}
                      {config?.accountId && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Account ID:</span>
                          <span className="font-mono">{config.accountId}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Access Key:</span>
                        <span className="font-mono">{config?.accessKeyId || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Secret Key:</span>
                        <span className="font-mono">{config?.secretAccessKey || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Bucket:</span>
                        <span className="font-mono">{config?.bucketName || '-'}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={handleTestConnection} disabled={testingConnection} className="flex-1">
                        {testingConnection ? (
                          <><RefreshCw className="mr-2 h-4 w-4 animate-spin" /> Testing...</>
                        ) : (
                          <><Zap className="mr-2 h-4 w-4" /> Test Connessione</>
                        )}
                      </Button>
                      <Button variant="outline" onClick={() => setShowR2Form(true)}>
                        <Settings className="mr-2 h-4 w-4" /> Modifica
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-muted-foreground/70 mb-1 flex items-center gap-1">
                        Endpoint S3
                        <span className="relative group">
                          <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block w-64 p-2 text-xs bg-slate-800 text-white rounded shadow-lg z-50">
                            Cloudflare Dashboard → R2 → Overview → nella sezione "Use jurisdiction-specific endpoints for S3 clients", copia l'URL EU
                          </span>
                        </span>
                      </label>
                      <Input
                        placeholder="https://xxxxx.r2.cloudflarestorage.com"
                        value={r2Form.endpoint}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setR2Form(f => ({ ...f, endpoint: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground/70 mb-1 flex items-center gap-1">
                        Chiave di Accesso (Access Key ID)
                        <span className="relative group">
                          <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block w-64 p-2 text-xs bg-slate-800 text-white rounded shadow-lg z-50">
                            Cloudflare Dashboard → R2 → Manage R2 API Tokens → crea token → copia "Access Key ID"
                          </span>
                        </span>
                      </label>
                      <Input
                        placeholder="es. eb0abfea27df..."
                        value={r2Form.accessKeyId}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setR2Form(f => ({ ...f, accessKeyId: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground/70 mb-1 flex items-center gap-1">
                        Chiave Segreta (Secret Access Key)
                        <span className="relative group">
                          <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block w-64 p-2 text-xs bg-slate-800 text-white rounded shadow-lg z-50">
                            Mostrata solo alla creazione del token R2 — se persa, genera un nuovo token
                          </span>
                        </span>
                      </label>
                      <Input
                        type="password"
                        placeholder="Chiave segreta R2"
                        value={r2Form.secretAccessKey}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setR2Form(f => ({ ...f, secretAccessKey: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground/70 mb-1 flex items-center gap-1">
                        Nome Bucket
                        <span className="relative group">
                          <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block w-64 p-2 text-xs bg-slate-800 text-white rounded shadow-lg z-50">
                            Il nome del bucket R2 creato su Cloudflare (es. "edgvoip")
                          </span>
                        </span>
                      </label>
                      <Input
                        placeholder="es. edgvoip"
                        value={r2Form.bucketName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setR2Form(f => ({ ...f, bucketName: e.target.value }))}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={handleSaveR2Config} disabled={savingR2Config} className="flex-1">
                        {savingR2Config ? (
                          <><RefreshCw className="mr-2 h-4 w-4 animate-spin" /> Verifica e Salva...</>
                        ) : (
                          <><CheckCircle2 className="mr-2 h-4 w-4" /> Verifica e Salva</>
                        )}
                      </Button>
                      {config?.configured && (
                        <Button variant="outline" onClick={() => setShowR2Form(false)}>Annulla</Button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">La connessione verrà testata automaticamente prima del salvataggio</p>
                  </div>
                )}
              </GlassCardContent>
            </GlassCard>

            <GlassCard>
              <GlassCardHeader>
                <GlassCardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" /> Health Check
                </GlassCardTitle>
              </GlassCardHeader>
              <GlassCardContent className="space-y-4">
                {config?.lastHealthCheck ? (
                  <>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-muted-foreground">Ultimo Check:</span>
                      {config.lastHealthCheck.status === 'connected' ? (
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                          <CheckCircle2 className="mr-1 h-3 w-3" /> Online
                        </Badge>
                      ) : (
                        <Badge variant="destructive">
                          <XCircle className="mr-1 h-3 w-3" /> Errore
                        </Badge>
                      )}
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Latenza:</span>
                        <span>{config.lastHealthCheck.latencyMs}ms</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Ultimo controllo:</span>
                        <span>{formatDate(config.lastHealthCheck.checkedAt)}</span>
                      </div>
                      {config.lastHealthCheck.error && (
                        <div className="p-2 bg-red-500/10 rounded text-red-400 text-xs">
                          {config.lastHealthCheck.error}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">Nessun health check eseguito</p>
                )}

                {overview?.r2Status && (
                  <div className="pt-4 border-t border-white/10 space-y-2 text-sm">
                    <h4 className="font-medium">Info Bucket</h4>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Spazio Totale:</span>
                      <span>{overview.totalUsedFormatted}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">File Totali:</span>
                      <span>{overview.totalFiles.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Costo Stimato:</span>
                      <span>${overview.estimatedCost}/mese</span>
                    </div>
                  </div>
                )}
              </GlassCardContent>
            </GlassCard>
          </div>
        </TabsContent>

        {/* TAB: Overview */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <GlassCard className="border-l-4 border-l-blue-500">
              <GlassCardContent className="pt-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Spazio Totale R2</span>
                  <HardDrive className="h-4 w-4 text-blue-500" />
                </div>
                <div className="text-2xl font-bold">{overview?.totalUsedFormatted || '0 B'}</div>
              </GlassCardContent>
            </GlassCard>

            <GlassCard className="border-l-4 border-l-purple-500">
              <GlassCardContent className="pt-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">File Totali</span>
                  <FileText className="h-4 w-4 text-purple-500" />
                </div>
                <div className="text-2xl font-bold">{overview?.totalFiles?.toLocaleString() || 0}</div>
              </GlassCardContent>
            </GlassCard>

            <GlassCard className="border-l-4 border-l-yellow-500">
              <GlassCardContent className="pt-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Sync Pending</span>
                  <RefreshCw className="h-4 w-4 text-yellow-500" />
                </div>
                <div className="text-2xl font-bold">{overview?.syncPending || 0}</div>
                {overview && overview.syncErrors24h > 0 && (
                  <span className="text-xs text-red-400">{overview.syncErrors24h} errori 24h</span>
                )}
              </GlassCardContent>
            </GlassCard>

            <GlassCard className="border-l-4 border-l-green-500">
              <GlassCardContent className="pt-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Costo Stimato</span>
                  <TrendingUp className="h-4 w-4 text-green-500" />
                </div>
                <div className="text-2xl font-bold">${overview?.estimatedCost || '0.00'}/mo</div>
                <span className="text-xs text-muted-foreground">$0.015/GB/mese</span>
              </GlassCardContent>
            </GlassCard>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <GlassCard>
              <GlassCardHeader>
                <GlassCardTitle>Breakdown Categorie</GlassCardTitle>
              </GlassCardHeader>
              <GlassCardContent>
                {breakdownPieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie data={breakdownPieData} cx="50%" cy="50%" outerRadius={100} dataKey="value"
                        label={(props: { name?: string; value?: number }) => `${props.name}: ${formatBytes(Number(props.value) || 0)}`}>
                        {breakdownPieData.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(val) => formatBytes(chartNum(val))} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">Nessun dato disponibile</div>
                )}
              </GlassCardContent>
            </GlassCard>

            <GlassCard>
              <GlassCardHeader>
                <GlassCardTitle>Top 5 Tenant per Spazio</GlassCardTitle>
              </GlassCardHeader>
              <GlassCardContent>
                {overview?.topTenants && overview.topTenants.length > 0 ? (
                  <div className="space-y-4">
                    {overview.topTenants.map((t) => (
                      <div key={t.tenantId} className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium">{t.tenantName}</span>
                          <span className="text-muted-foreground">{formatBytes(t.usedBytes)} / {formatBytes(t.maxBytes)}</span>
                        </div>
                        <Progress value={t.percentage} className="h-2" />
                        <div className="flex gap-2 text-xs text-muted-foreground">
                          <span>Rec: {formatBytes(t.recordings)}</span>
                          <span>Vid: {formatBytes(t.videos)}</span>
                          <span>Doc: {formatBytes(t.documents)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">Nessun tenant</div>
                )}
              </GlassCardContent>
            </GlassCard>
          </div>

          {overview?.r2Status && (
            <GlassCard>
              <GlassCardHeader>
                <GlassCardTitle>Stato Sync Globale</GlassCardTitle>
              </GlassCardHeader>
              <GlassCardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <span className="text-sm text-muted-foreground">R2 Status</span>
                    <div className="flex items-center gap-2 mt-1">
                      {overview.r2Status.status === 'connected' ? (
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Online</Badge>
                      ) : (
                        <Badge variant="destructive">Offline</Badge>
                      )}
                    </div>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Latenza R2</span>
                    <div className="text-lg font-semibold mt-1">{overview.r2Status.latencyMs}ms</div>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">File in Coda</span>
                    <div className="text-lg font-semibold mt-1">{overview.syncPending}</div>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Bytes in Coda</span>
                    <div className="text-lg font-semibold mt-1">{formatBytes(overview.syncPendingBytes)}</div>
                  </div>
                </div>
              </GlassCardContent>
            </GlassCard>
          )}
        </TabsContent>

        {/* TAB: Analytics */}
        <TabsContent value="analytics" className="space-y-6">
          <GlassCard>
            <GlassCardContent className="pt-6">
              <div className="flex items-center gap-4">
                <Select value={analyticsScope} onValueChange={setAnalyticsScope}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Scope" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="global">Globale</SelectItem>
                    <SelectItem value="tenant">Per Tenant</SelectItem>
                  </SelectContent>
                </Select>
                {analyticsScope === 'tenant' && (
                  <Select value={analyticsTenantId} onValueChange={(v) => { setAnalyticsTenantId(v); }}>
                    <SelectTrigger className="w-64">
                      <SelectValue placeholder="Seleziona tenant..." />
                    </SelectTrigger>
                    <SelectContent>
                      {allTenantsList.map(t => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Button variant="outline" onClick={loadAnalytics}>
                  <RefreshCw className="mr-2 h-4 w-4" /> Aggiorna
                </Button>
              </div>
            </GlassCardContent>
          </GlassCard>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <GlassCard>
              <GlassCardHeader>
                <GlassCardTitle>Distribuzione Categorie</GlassCardTitle>
              </GlassCardHeader>
              <GlassCardContent>
                {analyticsPieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie data={analyticsPieData} cx="50%" cy="50%" innerRadius={60} outerRadius={100}
                        dataKey="value" label={(p: { name?: string; value?: number }) => `${p.name}: ${formatBytes(Number(p.value) || 0)}`}>
                        {analyticsPieData.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(val) => formatBytes(chartNum(val))} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">Nessun dato</div>
                )}
              </GlassCardContent>
            </GlassCard>

            <GlassCard>
              <GlassCardHeader>
                <GlassCardTitle>Upload Giornalieri</GlassCardTitle>
              </GlassCardHeader>
              <GlassCardContent>
                {dailyUploadAggregated.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={dailyUploadAggregated}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" tickFormatter={(d: string) => d.slice(5)} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="recording" fill={COLORS[0]} name="Registrazioni" stackId="a" />
                      <Bar dataKey="video" fill={COLORS[1]} name="Video" stackId="a" />
                      <Bar dataKey="document" fill={COLORS[3]} name="Documenti" stackId="a" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">Nessun dato</div>
                )}
              </GlassCardContent>
            </GlassCard>

            <GlassCard>
              <GlassCardHeader>
                <GlassCardTitle>Dimensione Media per Categoria</GlassCardTitle>
              </GlassCardHeader>
              <GlassCardContent>
                {analytics?.avgFileSize && analytics.avgFileSize.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={analytics.avgFileSize.map(a => ({
                      category: CATEGORY_LABELS[a.category] || a.category,
                      avgSize: a.avgSize,
                      count: a.count
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="category" />
                      <YAxis tickFormatter={(v: number) => formatBytes(v)} />
                      <Tooltip formatter={(val) => formatBytes(chartNum(val))} />
                      <Bar dataKey="avgSize" fill={COLORS[4]} name="Dimensione media" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">Nessun dato</div>
                )}
              </GlassCardContent>
            </GlassCard>

            <GlassCard>
              <GlassCardHeader>
                <GlassCardTitle>Crescita Storage (30gg)</GlassCardTitle>
              </GlassCardHeader>
              <GlassCardContent>
                {dailyUploadAggregated.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={dailyUploadAggregated}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" tickFormatter={(d: string) => d.slice(5)} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Area type="monotone" dataKey="recording" fill={COLORS[0]} stroke={COLORS[0]} name="Registrazioni" stackId="1" fillOpacity={0.3} />
                      <Area type="monotone" dataKey="video" fill={COLORS[1]} stroke={COLORS[1]} name="Video" stackId="1" fillOpacity={0.3} />
                      <Area type="monotone" dataKey="document" fill={COLORS[3]} stroke={COLORS[3]} name="Documenti" stackId="1" fillOpacity={0.3} />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">Nessun dato</div>
                )}
              </GlassCardContent>
            </GlassCard>
          </div>

          <GlassCard>
            <GlassCardHeader>
              <GlassCardTitle>Top 20 File più Pesanti</GlassCardTitle>
            </GlassCardHeader>
            <GlassCardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left p-3">File</th>
                      <th className="text-left p-3">Dimensione</th>
                      <th className="text-left p-3">Categoria</th>
                      <th className="text-left p-3">Tenant</th>
                      <th className="text-left p-3">Data</th>
                      <th className="text-center p-3">Sync</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics?.topFiles?.map(f => {
                      const CatIcon = CATEGORY_ICONS[f.category] || FileText;
                      return (
                        <tr key={f.id} className="border-b border-white/5 hover:bg-white/5">
                          <td className="p-3 font-mono text-xs truncate max-w-[200px]">{f.fileName}</td>
                          <td className="p-3">{f.fileSizeFormatted}</td>
                          <td className="p-3">
                            <div className="flex items-center gap-1">
                              <CatIcon className="h-3 w-3" />
                              {CATEGORY_LABELS[f.category] || f.category}
                            </div>
                          </td>
                          <td className="p-3">{f.tenantName}</td>
                          <td className="p-3">{formatDate(f.createdAt)}</td>
                          <td className="p-3 text-center">
                            {f.cloudSynced ? (
                              <CheckCircle2 className="h-4 w-4 text-green-400 inline" />
                            ) : (
                              <Clock className="h-4 w-4 text-yellow-400 inline" />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {(!analytics?.topFiles || analytics.topFiles.length === 0) && (
                      <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">Nessun file</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </GlassCardContent>
          </GlassCard>
        </TabsContent>

        {/* TAB: Tenants */}
        <TabsContent value="tenants" className="space-y-6">
          <GlassCard>
            <GlassCardContent className="pt-6">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Cerca tenant..."
                      value={tenantSearch}
                      onChange={e => setTenantSearch(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <Select value={tenantFilter} onValueChange={setTenantFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Filtro" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti</SelectItem>
                    <SelectItem value="alert">Warning (&gt;80%)</SelectItem>
                    <SelectItem value="critical">Critici (&gt;90%)</SelectItem>
                    <SelectItem value="empty">Vuoti</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={tenantSort} onValueChange={setTenantSort}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Ordina per" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="used_bytes">Spazio usato</SelectItem>
                    <SelectItem value="percentage">% usata</SelectItem>
                    <SelectItem value="name">Nome</SelectItem>
                    <SelectItem value="max_bytes">Quota max</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={() => setTenantSortDir(d => d === 'asc' ? 'desc' : 'asc')}>
                  {tenantSortDir === 'desc' ? '↓' : '↑'}
                </Button>
                <Button variant="outline" onClick={handleExportCSV}>
                  <Download className="mr-2 h-4 w-4" /> Export CSV
                </Button>
              </div>
            </GlassCardContent>
          </GlassCard>

          <GlassCard>
            <GlassCardContent className="pt-6">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-center p-3 w-16">Alert</th>
                      <th className="text-left p-3">Tenant</th>
                      <th className="text-right p-3">Quota</th>
                      <th className="text-right p-3">Usato</th>
                      <th className="text-right p-3">%</th>
                      <th className="text-right p-3">Rec</th>
                      <th className="text-right p-3">Doc</th>
                      <th className="text-right p-3">Video</th>
                      <th className="text-center p-3">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenants.map(t => (
                      <tr key={t.tenantId} className="border-b border-white/5 hover:bg-white/5">
                        <td className="p-3 text-center">{alertLevelBadge(t.alertLevel)}</td>
                        <td className="p-3">
                          <div className="font-medium">{t.tenantName}</div>
                          <div className="text-xs text-muted-foreground">{t.tenantSlug}</div>
                        </td>
                        <td className="p-3 text-right font-mono text-xs">{formatBytes(t.maxBytes)}</td>
                        <td className="p-3 text-right font-mono text-xs">{formatBytes(t.usedBytes)}</td>
                        <td className="p-3 text-right">
                          <div className="flex items-center gap-2 justify-end">
                            <Progress value={t.percentage} className="w-16 h-2" />
                            <span className="text-xs w-12 text-right">{t.percentage}%</span>
                          </div>
                        </td>
                        <td className="p-3 text-right font-mono text-xs">{formatBytes(t.recordings)}</td>
                        <td className="p-3 text-right font-mono text-xs">{formatBytes(t.documents)}</td>
                        <td className="p-3 text-right font-mono text-xs">{formatBytes(t.videos)}</td>
                        <td className="p-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => navigate(`/storage/tenant/${t.tenantId}`)} title="Dettaglio">
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => openQuotaDialog(t)} title="Modifica Quota">
                              <Settings className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleForceSync(t.tenantId)} title="Forza Sync">
                              <RefreshCw className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleForceCleanup(t.tenantId)} title="Cleanup">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {tenants.length === 0 && (
                      <tr><td colSpan={10} className="p-8 text-center text-muted-foreground">Nessun tenant trovato</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {tenantsPagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
                  <span className="text-sm text-muted-foreground">
                    Pagina {tenantsPagination.page} di {tenantsPagination.totalPages} ({tenantsPagination.total} totali)
                  </span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={tenantsPagination.page <= 1}
                      onClick={() => loadTenants(tenantsPagination.page - 1)}>
                      Precedente
                    </Button>
                    <Button variant="outline" size="sm" disabled={tenantsPagination.page >= tenantsPagination.totalPages}
                      onClick={() => loadTenants(tenantsPagination.page + 1)}>
                      Successiva
                    </Button>
                  </div>
                </div>
              )}
            </GlassCardContent>
          </GlassCard>
        </TabsContent>

        {/* TAB: Alerts Log */}
        <TabsContent value="alerts" className="space-y-6">
          <GlassCard>
            <GlassCardContent className="pt-6">
              <div className="flex items-center gap-4">
                <Select value={alertTenantFilter} onValueChange={setAlertTenantFilter}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Tenant" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti i Tenant</SelectItem>
                    {allTenantsList.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={alertSeverityFilter} onValueChange={setAlertSeverityFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Severity" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte</SelectItem>
                    <SelectItem value="critical">Critico</SelectItem>
                    <SelectItem value="high">Alto</SelectItem>
                    <SelectItem value="warning">Warning</SelectItem>
                    <SelectItem value="info">Info</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={loadAlerts}>
                  <RefreshCw className="mr-2 h-4 w-4" /> Aggiorna
                </Button>
              </div>
            </GlassCardContent>
          </GlassCard>

          <GlassCard>
            <GlassCardContent className="pt-6">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left p-3">Data</th>
                      <th className="text-left p-3">Tenant</th>
                      <th className="text-left p-3">Tipo</th>
                      <th className="text-center p-3">Severity</th>
                      <th className="text-left p-3">Dettaglio</th>
                      <th className="text-center p-3">Risolto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {alerts.map(a => (
                      <tr key={a.id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="p-3 text-xs">{formatDate(a.createdAt)}</td>
                        <td className="p-3">{a.tenantName || '-'}</td>
                        <td className="p-3">{a.alertType}</td>
                        <td className="p-3 text-center">{severityBadge(a.severity)}</td>
                        <td className="p-3 text-xs truncate max-w-[300px]">{a.title}</td>
                        <td className="p-3 text-center">
                          {a.resolved ? (
                            <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                              <CheckCircle2 className="mr-1 h-3 w-3" /> Sì
                            </Badge>
                          ) : (
                            <Badge variant="outline">No</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                    {alerts.length === 0 && (
                      <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">Nessun alert negli ultimi 30 giorni</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </GlassCardContent>
          </GlassCard>
        </TabsContent>
      </Tabs>

      <Dialog open={quotaDialogOpen} onOpenChange={setQuotaDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Modifica Quota — {quotaDialogTenant?.tenantName}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Quota Globale (bytes)</Label>
              <Input type="number" value={quotaForm.maxBytes}
                onChange={e => setQuotaForm(f => ({ ...f, maxBytes: Number(e.target.value) }))} />
              <span className="text-xs text-muted-foreground">{formatBytes(quotaForm.maxBytes)}</span>
            </div>
            <div>
              <Label>Max Registrazioni (bytes)</Label>
              <Input type="number" value={quotaForm.recordingsMaxBytes}
                onChange={e => setQuotaForm(f => ({ ...f, recordingsMaxBytes: Number(e.target.value) }))} />
              <span className="text-xs text-muted-foreground">{formatBytes(quotaForm.recordingsMaxBytes)}</span>
            </div>
            <div>
              <Label>Retention Registrazioni (giorni)</Label>
              <Input type="number" value={quotaForm.recordingsRetentionDays}
                onChange={e => setQuotaForm(f => ({ ...f, recordingsRetentionDays: Number(e.target.value) }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuotaDialogOpen(false)}>Annulla</Button>
            <Button onClick={handleSaveQuota}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
